// FILE: src/components/QuestionPreviewModal.js
//
// Phase 3.6 — admin-only preview of a single question rendered exactly the
// way a learner sees it during a session.  Used both by the Question Editor
// (in-modal "Preview as Learner" overlay) and by the App-level deep-link
// handler (when the URL carries ?preview=<id>&exam=<examId>).
//
// ── Props ───────────────────────────────────────────────────────────────────
//
//   question      {object}   — normalized question object (same shape as
//                              session.questions[i])
//   onClose       {function} — called when the user dismisses the preview
//   variant       'modal'|'page'  (default 'modal')
//                              'modal' renders a centered overlay over the
//                              admin app; 'page' renders full-screen for the
//                              deep-link handler.
//
// ── Behaviour ───────────────────────────────────────────────────────────────
//
//   The modal owns the interactive state for the preview (selectedAnswers,
//   submitted, eliminations) so admins can click choices, submit, and see
//   the correct/incorrect feedback + explanation exactly as a learner would.
//
//   A "Reset" button clears interactive state without re-mounting, so admins
//   can quickly try multiple answer paths.
//
//   The modal is dismissed by clicking the backdrop, the X close button, or
//   pressing Escape.

import React, { useState, useEffect, useCallback } from 'react';
import QuestionCard from '../session/QuestionCard';
import ExplanationPanel from '../session/ExplanationPanel';

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '32px 16px',
    overflow: 'auto',
    zIndex: 1000,
  },
  shell: {
    background: '#f5f5f5',
    border: '1px solid #ccc',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '880px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    background: '#1a1a2e',
    color: '#fff',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
  title: {
    flex: 1,
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '0.3px',
  },
  hintRow: {
    fontSize: '0.78rem',
    color: '#cbd5e1',
    fontWeight: 400,
  },
  headerButton: {
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#cbd5e1',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  closeButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #c62828',
    borderRadius: '4px',
    color: '#ef9a9a',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  body: {
    flex: 1,
    padding: '24px',
    background: '#f5f5f5',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  unavailable: {
    padding: '32px 16px',
    textAlign: 'center',
    color: '#888',
    fontStyle: 'italic',
  },
};

export default function QuestionPreviewModal({ question, onClose, variant = 'modal' }) {
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [submitted,       setSubmitted]       = useState(false);
  const [eliminated,      setEliminated]      = useState([]);

  const handleReset = useCallback(() => {
    setSelectedAnswers([]);
    setSubmitted(false);
    setEliminated([]);
  }, []);

  // Reset interactive state whenever the underlying question changes, so a
  // long-lived modal that swaps between questions doesn't carry stale
  // selections forward.
  useEffect(() => { handleReset(); }, [question?.id, handleReset]);

  // Esc closes the modal.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && typeof onClose === 'function') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onBackdropClick = (e) => {
    // Only close when the click was on the backdrop itself, not bubbled up
    // from the shell.
    if (e.target === e.currentTarget && typeof onClose === 'function') onClose();
  };

  const toggleEliminate = (id) => {
    setEliminated((prev) => prev.includes(id)
      ? prev.filter((x) => x !== id)
      : [...prev, id]);
  };

  // Backdrop styles for the page variant collapse to a plain stage so the
  // preview takes the full viewport without the "modal" framing.
  const backdropStyle = variant === 'page'
    ? { ...styles.backdrop, position: 'static', background: '#f5f5f5', padding: 0, alignItems: 'stretch' }
    : styles.backdrop;
  const shellStyle = variant === 'page'
    ? { ...styles.shell, maxWidth: '100%', borderRadius: 0, boxShadow: 'none', border: 'none' }
    : styles.shell;

  return (
    <div
      data-testid="question-preview-modal"
      data-variant={variant}
      style={backdropStyle}
      onClick={onBackdropClick}
    >
      <div style={shellStyle} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <span style={styles.title}>
            👁 Question Preview
            {question?.referenceId && (
              <span style={{ ...styles.hintRow, marginLeft: '12px' }}>
                {question.referenceId}
              </span>
            )}
          </span>
          <button
            data-testid="question-preview-reset-button"
            type="button"
            style={styles.headerButton}
            onClick={handleReset}
          >
            ↺ Reset
          </button>
          <button
            data-testid="question-preview-close-button"
            type="button"
            style={styles.closeButton}
            onClick={onClose}
          >
            ✕ Close
          </button>
        </div>

        <div style={styles.body}>
          {!question ? (
            <div style={styles.unavailable} data-testid="question-preview-empty">
              No question to preview.
            </div>
          ) : (
            <>
              <QuestionCard
                question={question}
                selectedAnswers={selectedAnswers}
                onSelectAnswer={setSelectedAnswers}
                onSubmit={() => setSubmitted(true)}
                onPrevious={() => {}}
                isPreviousDisabled={true}
                onSkip={() => {}}
                isSkipped={false}
                isSubmitted={submitted}
                eliminatedChoices={eliminated}
                onToggleElimination={toggleEliminate}
              />
              {submitted && (
                <ExplanationPanel
                  question={question}
                  selectedAnswers={selectedAnswers}
                  onNext={handleReset}
                  mode="practice"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Builds the canonical preview URL for a given question.  Used by the editor
 * to populate a "Copy preview link" button.  Exported so tests can verify
 * the format without rendering.
 */
export function buildPreviewUrl(questionId, examId, baseUrl = window.location.origin) {
  if (!questionId) return null;
  const params = new URLSearchParams();
  params.set('preview', String(questionId));
  if (examId) params.set('exam', String(examId));
  return `${baseUrl}/?${params.toString()}`;
}
